import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Megaphone, Play, Pause, Send, AlertTriangle, Users, Search } from "lucide-react";
import { PageHeader, Button, Input, Textarea, Select, Modal, Card, CardContent, StatusBadge, LoadingBlock, EmptyState, Checkbox, toast } from "@/components/ds";
import { api, formatApiError, type Campaign, type CampaignConfig } from "@/lib/api";

const STATE_TONE: Record<string, "slate" | "blue" | "green" | "amber"> = {
  draft: "slate", scheduled: "blue", active: "green", paused: "amber", completed: "slate",
};

export default function Campaigns() {
  const [config, setConfig] = useState<CampaignConfig | null>(null);
  const [rows, setRows] = useState<Campaign[] | null>(null);
  const [showCreate, setShowCreate] = useState(false);

  const load = useCallback(() => { api.campaignsList().then(setRows).catch((e) => toast.error(formatApiError(e))); }, []);
  useEffect(() => { api.campaignsConfig().then(setConfig).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);

  const act = async (fn: Promise<unknown>, ok: string) => {
    try { await fn; toast.success(ok); load(); } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="page-campaigns">
      <PageHeader title="Reactivation Campaigns" description="Bring past customers back with seasonal tune-ups, maintenance and inspection reminders." testid="campaigns-header"
        action={config?.included ? <Button onClick={() => setShowCreate(true)} data-testid="campaign-new-btn"><Megaphone className="h-4 w-4" /> New campaign</Button> : undefined} />

      {config && (
        <div className="flex flex-wrap items-center gap-3 rounded-xl border border-slate-200 bg-white p-4 shadow-sm" data-testid="campaigns-config">
          <StatusBadge tone="blue">{config.plan} plan</StatusBadge>
          {config.included ? <StatusBadge tone="green">Included · up to {config.max_active} active · {config.max_audience.toLocaleString()} audience</StatusBadge>
            : <StatusBadge tone="amber">Not included — upgrade to Growth+</StatusBadge>}
          {config.included && (config.sms_configured ? <StatusBadge tone="green">SMS live</StatusBadge>
            : <StatusBadge tone="amber"><AlertTriangle className="mr-1 inline h-3 w-3" /> SMS: configuration required</StatusBadge>)}
        </div>
      )}

      {config && !config.included && (
        <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-blue-700" data-testid="campaigns-upgrade">
          Reactivation campaigns are available on Growth and above. Upgrade to build audiences from your past customers and re-engage them automatically.
        </div>
      )}
      {config?.included && !config?.sms_configured && (
        <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3" data-testid="campaigns-honest-note">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
          <p className="text-xs text-amber-800">Campaigns build and schedule normally, but SMS sending is paused until Twilio is live. Recipients are marked <b>“Unavailable — Configuration Required”</b> and are <b>never</b> recorded as sent.</p>
        </div>
      )}

      {rows === null ? <LoadingBlock rows={4} /> : rows.length === 0 ? (
        <EmptyState icon={Megaphone} title="No campaigns yet" description="Create a reactivation campaign to re-engage your past customers." />
      ) : (
        <div className="space-y-3" data-testid="campaigns-list">
          {rows.map((c) => (
            <Card key={c.id} data-testid={`campaign-${c.id}`}>
              <CardContent className="p-4">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div>
                    <p className="font-medium text-slate-900">{c.name}</p>
                    <div className="mt-1 flex items-center gap-2">
                      <StatusBadge tone={STATE_TONE[c.status] ?? "slate"}>{c.status}</StatusBadge>
                      <span className="text-xs text-slate-500">{c.channel.toUpperCase()} · {c.stats.total} recipients</span>
                      {c.scheduled_at && <span className="text-xs text-slate-400">scheduled {new Date(c.scheduled_at).toLocaleString()}</span>}
                    </div>
                    <p className="mt-2 max-w-xl text-xs text-slate-500 line-clamp-2">{c.message}</p>
                    {c.stats.total > 0 && (
                      <div className="mt-2 flex gap-3 text-xs text-slate-500" data-testid={`campaign-stats-${c.id}`}>
                        <span>Sent <b className="text-emerald-700">{c.stats.sent}</b></span>
                        <span>Unavailable <b className="text-amber-700">{c.stats.unavailable}</b></span>
                        <span>Opted-out <b className="text-slate-600">{c.stats.skipped}</b></span>
                        <span>Pending <b className="text-blue-700">{c.stats.pending}</b></span>
                      </div>
                    )}
                  </div>
                  <div className="flex gap-2">
                    {(c.status === "draft" || c.status === "paused") && <Button size="sm" onClick={() => act(api.campaignActivate(c.id), "Campaign activated")} data-testid={`campaign-activate-${c.id}`}><Play className="h-3.5 w-3.5" /> {c.status === "paused" ? "Resume" : "Activate"}</Button>}
                    {(c.status === "active" || c.status === "scheduled") && <Button size="sm" variant="outline" onClick={() => act(api.campaignPause(c.id), "Campaign paused")} data-testid={`campaign-pause-${c.id}`}><Pause className="h-3.5 w-3.5" /> Pause</Button>}
                    {c.status === "active" && <Button size="sm" variant="outline" onClick={() => act(api.campaignProcess(c.id), "Processing recipients")} data-testid={`campaign-process-${c.id}`}><Send className="h-3.5 w-3.5" /> Send now</Button>}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {showCreate && config && <CreateCampaign config={config} onClose={() => setShowCreate(false)} onCreated={load} />}
    </div>
  );
}

function CreateCampaign({ config, onClose, onCreated }: { config: CampaignConfig; onClose: () => void; onCreated: () => void }) {
  const [name, setName] = useState("");
  const [message, setMessage] = useState("");
  const [channel, setChannel] = useState("sms");
  const [contacts, setContacts] = useState<any[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.crmContacts().then((d: any) => setContacts(d)).catch(() => {}); }, []);
  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    return contacts.filter((c) => !t || `${c.first_name} ${c.last_name ?? ""}`.toLowerCase().includes(t) || (c.phone ?? "").includes(t));
  }, [contacts, q]);

  const toggle = (id: string) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const applyTemplate = (key: string) => {
    const t = config.templates.find((x) => x.key === key);
    if (t) { setName(t.name); setMessage(t.message); }
  };

  const create = async () => {
    if (!name || !message || selected.size === 0) { toast.error("Add a name, message and at least one contact"); return; }
    setBusy(true);
    try {
      await api.campaignCreate({ name, message, channel, contact_ids: Array.from(selected) });
      toast.success("Campaign created as draft"); onCreated(); onClose();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const channelOpts = [{ value: "sms", label: "SMS" }, ...(config.voice_available ? [{ value: "voice", label: "Voice" }] : [])];

  return (
    <Modal open onOpenChange={(o) => !o && onClose()} title="New reactivation campaign"
      description="Pick a seasonal template, choose your audience, and save as a draft." testid="campaign-create-modal">
      <div className="max-h-[74vh] space-y-4 overflow-y-auto pr-1">
        <div>
          <p className="mb-1 text-xs font-medium text-slate-500">Seasonal template</p>
          <div className="flex flex-wrap gap-2">
            {config.templates.map((t) => (
              <button key={t.key} onClick={() => applyTemplate(t.key)} className="rounded-full border border-slate-200 px-3 py-1 text-xs hover:border-blue-400 hover:bg-blue-50" data-testid={`campaign-template-${t.key}`}>{t.name}</button>
            ))}
          </div>
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <div><p className="mb-1 text-xs font-medium text-slate-500">Campaign name</p><Input value={name} onChange={(e) => setName(e.target.value)} data-testid="campaign-name" /></div>
          <div><p className="mb-1 text-xs font-medium text-slate-500">Channel</p><Select value={channel} onChange={setChannel} options={channelOpts} testid="campaign-channel" /></div>
        </div>
        <div>
          <p className="mb-1 text-xs font-medium text-slate-500">Message <span className="text-slate-400">(use {"{first_name}"} and {"{business}"})</span></p>
          <Textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={3} data-testid="campaign-message" />
          <p className="mt-1 text-[11px] text-slate-400">An opt-out line (Reply STOP) is included; contacts marked do-not-text are automatically excluded.</p>
        </div>
        <div>
          <div className="mb-1 flex items-center justify-between">
            <p className="text-xs font-medium text-slate-500">Audience <span className="text-slate-400">· {selected.size} selected</span></p>
            <div className="relative w-48"><Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" /><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search contacts" className="pl-7 text-xs" data-testid="campaign-contact-search" /></div>
          </div>
          <div className="max-h-56 space-y-1 overflow-y-auto rounded-lg border border-slate-200 p-2" data-testid="campaign-contact-list">
            {filtered.length === 0 ? <p className="p-2 text-xs text-slate-400">No contacts found.</p> : filtered.slice(0, 200).map((c) => (
              <label key={c.id} className="flex items-center gap-2 rounded px-2 py-1 hover:bg-slate-50" data-testid={`campaign-contact-${c.id}`}>
                <Checkbox checked={selected.has(c.id)} onChange={() => toggle(c.id)} />
                <span className="text-sm text-slate-700">{c.first_name} {c.last_name ?? ""}</span>
                <span className="ml-auto text-xs text-slate-400">{c.phone ?? "no phone"}</span>
              </label>
            ))}
          </div>
        </div>
      </div>
      <div className="mt-4 flex justify-end gap-2 border-t border-slate-100 pt-4">
        <Button variant="outline" onClick={onClose}>Cancel</Button>
        <Button onClick={create} disabled={busy} data-testid="campaign-save-btn"><Users className="h-4 w-4" /> Create draft ({selected.size})</Button>
      </div>
    </Modal>
  );
}
